frappe.provide("payments");

// ── Entry point ─────────────────────────────────────────────────────────────

/**
 * Render a "Payment Configuration" dashboard section on a gateway settings form.
 * Call from the `refresh` event of any *Settings doctype acting as a gateway controller.
 */
payments.render_gateway_setup_section = function (frm) {
	if (frm.doc.__islocal) return;

	let args = { doctype: frm.doctype, name: frm.doc.name }
	if (frappe.model.is_single(frm.doctype)) {
		args = { doctype: frm.doctype }
	}

	frappe.call({
		method: "payments.utils.gateway_setup.get_gateway_setup_data",
		args: args,
		callback(r) {
			if (!r || !r.message) return;
			const html = payments._build_gateway_setup_html(r.message);
			// add_section returns the body jQuery element
			const sectionBody = frm.dashboard.add_section(html, __("Payment Configuration"));
			frm.dashboard.show();
			// Bind events directly on the returned container
			const container = sectionBody instanceof $ ? sectionBody[0] : sectionBody;
			payments._bind_events(frm, r.message, container);
		},
	});
};

// ── HTML builder ────────────────────────────────────────────────────────────

payments._build_gateway_setup_html = function (data) {
	return `
		<div class="gws-section">
			${payments._build_gateway_row(data)}
			${payments._build_accounts_section(data)}
			<div class="gws-divider"></div>
			${payments._build_mop_section(data)}
		</div>`;
};

payments._build_gateway_row = function (data) {
	if (data.payment_gateway) {
		const link = `/app/payment-gateway/${encodeURIComponent(data.payment_gateway)}`;
		return `
			<div class="gws-gateway-row mb-3">
				<span class="gws-pill gws-pill-green">${frappe.utils.icon("check", "xs")} ${__("Configured")}</span>
				<span class="gws-gateway-name">
					<a href="${link}" target="_blank">${data.payment_gateway}</a>
				</span>
			</div>`;
	}
	return `
		<div class="gws-gateway-row mb-3">
			<span class="gws-pill gws-pill-orange">${frappe.utils.icon("alert", "xs")} ${__("Not configured")}</span>
			<span class="text-muted" style="flex:1">${__("Save this document to create the Payment Gateway record.")}</span>
		</div>`;
};

payments._build_accounts_section = function (data) {
	let inner;

	if (data.accounts && data.accounts.length) {
		const cards = data.accounts
			.map((a) => {
				const link = `/app/payment-gateway-account/${encodeURIComponent(a.name)}`;
				const defaultBadge = a.is_default
					? `<span class="gws-pill gws-pill-blue">${__("Default")}</span>`
					: "";
				const accountVal = a.payment_account
					? `<a href="/app/account/${encodeURIComponent(a.payment_account)}" target="_blank">${a.payment_account}</a>`
					: `<span class="gws-pill gws-pill-red">${__("Not set")}</span>`;
				const editBtn = `
					<button class="gws-icon-btn" data-action="edit-account" data-account="${a.name}" title="${__("Edit payment account")}">
						${frappe.utils.icon("edit", "xs")}
					</button>`;
				return `
					<div class="gws-account-card">
						<div class="gws-account-card-header">
							<span class="gws-account-card-company" title="${a.company || ""}">${a.company || __("(No company)")}</span>
							<div class="d-flex align-items-center gap-1">
								${defaultBadge}
								<a href="${link}" target="_blank" class="gws-icon-btn" title="${__("Open record")}">
									${frappe.utils.icon("link-url", "xs")}
								</a>
							</div>
						</div>
						<div class="gws-account-card-body">
							<div class="gws-account-field">
								<span>${__("Account:")}</span>
								<span class="gws-account-value" style="flex:1;min-width:0">${accountVal}</span>
								${editBtn}
							</div>
							<div class="gws-tags">
								${a.currency ? `<span class="gws-pill gws-pill-gray">${a.currency}</span>` : ""}
								${a.payment_channel ? `<span class="gws-pill gws-pill-gray">${a.payment_channel}</span>` : ""}
							</div>
						</div>
					</div>`;
			})
			.join("");

		inner = `<div class="gws-accounts-grid">${cards}</div>`;
	} else {
		inner = `
			<div class="gws-empty">
				${frappe.utils.icon("bank", "md")}
				<div>${__("No Payment Gateway Account configured yet.")}</div>
				<div class="text-muted" style="font-size:12px">${__("A payment account is required for each company that uses this gateway.")}</div>
				${
					data.payment_gateway
						? `<button class="btn btn-xs btn-default mt-1" data-action="add-gateway-account">
								${frappe.utils.icon("add", "xs")} ${__("Add Payment Account")}
							</button>`
						: ""
				}
			</div>`;
	}

	return `
		<div class="mb-3">
			<div class="gws-section-header">
				<span class="gws-section-label">${__("Payment Accounts")}</span>
				${
					data.payment_gateway && data.accounts && data.accounts.length
						? `<button class="btn btn-xs btn-default" data-action="add-gateway-account">
								${frappe.utils.icon("add", "xs")} ${__("Add")}
							</button>`
						: ""
				}
			</div>
			${inner}
		</div>`;
};

payments._build_mop_section = function (data) {
	let inner;

	if (data.modes_of_payment && data.modes_of_payment.length) {
		const rows = data.modes_of_payment
			.flatMap((mop) =>
				mop.accounts.map((acc) => {
					const mopLink = `/app/mode-of-payment/${encodeURIComponent(mop.name)}`;
					const accountVal = acc.default_account
						? `<span class="gws-mop-account">${acc.default_account}</span>`
						: `<span class="gws-pill gws-pill-red">${__("Account not set")}</span>`;
					const feeChip = acc.fee_account
						? `<span class="gws-pill gws-pill-gray" title="${__("Fee account")}">${frappe.utils.icon("percentage", "xs")} ${acc.fee_account}</span>`
						: `<span class="gws-pill gws-pill-orange" title="${__("Fee account not set")}">${__("No fee account")}</span>`;
					const taxChip = acc.tax_account
						? `<span class="gws-pill gws-pill-gray" title="${__("Tax account")}">${frappe.utils.icon("file", "xs")} ${acc.tax_account}</span>`
						: `<span class="gws-pill gws-pill-orange" title="${__("Tax account not set")}">${__("No tax account")}</span>`;

					return `
						<div class="gws-mop-row">
							<span class="gws-mop-name">
								<a href="${mopLink}" target="_blank">${mop.name}</a>
							</span>
							<span class="gws-mop-meta">
								<span class="text-muted">${acc.company || ""}</span>
								<span>·</span>
								${accountVal}
								${feeChip}
								${taxChip}
							</span>
							<a href="${mopLink}" target="_blank" class="gws-icon-btn" title="${__("Open")}">
								${frappe.utils.icon("arrow-right", "xs")}
							</a>
						</div>`;
				})
			)
			.join("");

		inner = `<div class="gws-mop-list">${rows}</div>`;
	} else {
		inner = `
			<div class="gws-empty">
				${frappe.utils.icon("cash", "md")}
				<div>${__("No Mode of Payment configured for this gateway.")}</div>
				<div class="text-muted" style="font-size:12px">
					${__("At least one Mode of Payment per company is required. Fee and tax accounts must also be configured.")}
				</div>
				<button class="btn btn-xs btn-default mt-1" data-action="configure-mop">
					${frappe.utils.icon("settings", "xs")} ${__("Configure Modes of Payment")}
				</button>
			</div>`;
	}

	return `
		<div class="mb-1">
			<div class="gws-section-header">
				<span class="gws-section-label">${__("Modes of Payment")}</span>
			</div>
			${inner}
		</div>`;
};

// ── Event binding ────────────────────────────────────────────────────────────

payments._bind_events = function (frm, data, container) {
	const section = container || document.querySelector(".gws-section");
	if (!section) return;

	section.querySelectorAll("[data-action='edit-account']").forEach((btn) => {
		btn.addEventListener("click", () => {
			payments._edit_payment_account(frm, btn.dataset.account, data);
		});
	});

	section.querySelectorAll("[data-action='add-gateway-account']").forEach((btn) => {
		btn.addEventListener("click", () => {
			payments._open_add_account_overlay(frm, data);
		});
	});

	const configureMopBtn = section.querySelector("[data-action='configure-mop']");
	if (configureMopBtn) {
		configureMopBtn.addEventListener("click", () => {
			payments._open_configure_mop_overlay(frm, data);
		});
	}
};

// ── Inline payment account editor ───────────────────────────────────────────

payments._edit_payment_account = function (frm, accountName, data) {
	const accountData = (data.accounts || []).find((a) => a.name === accountName);

	frappe.prompt(
		[
			{
				label: __("Payment Account"),
				fieldname: "payment_account",
				fieldtype: "Link",
				options: "Account",
				default: accountData?.payment_account || "",
				reqd: 1,
				description: __("Select the bank/cash account for this gateway account."),
				filters: { company: accountData?.company },
			},
		],
		(values) => {
			frappe.call({
				method: "payments.utils.gateway_setup.update_payment_account",
				args: { account_name: accountName, payment_account: values.payment_account },
				callback(r) {
					if (r.exc) return;
					frappe.show_alert({ message: __("Payment account updated"), indicator: "green" });
					// Re-render the section with fresh data
					payments.render_gateway_setup_section(frm);
				},
			});
		},
		__("Edit Payment Account — {0}", [accountData?.company || accountName]),
		__("Save")
	);
};

// ── Add Gateway Account overlay ─────────────────────────────────────────────

payments._open_add_account_overlay = function (frm, data) {
	// Build or reuse the overlay
	if (!payments._account_overlay) {
		const parent = $(frm.page.wrapper).find(".page-overlay");
		payments._account_overlay = new frappe.views.dodock_drawer({
			title: __("Add Payment Account"),
			parent: parent,
			animation_type: "slide",
			backdrop: true,
			open_width: "380px",
		});
	}

	const overlay = payments._account_overlay;

	// --- Body ---
	overlay.mainSection.innerHTML = "";
	const body = document.createElement("div");
	body.classList.add("gws-overlay-body");
	overlay.mainSection.appendChild(body);

	const subtitle = document.createElement("p");
	subtitle.classList.add("text-muted", "mb-3");
	subtitle.style.fontSize = "12px";
	subtitle.textContent = __(
		"Configure a payment account for a company that will use this gateway."
	);
	body.appendChild(subtitle);

	const formWrapper = document.createElement("div");
	body.appendChild(formWrapper);

	const fieldGroup = new frappe.ui.FieldGroup({
		fields: [
			{
				label: __("Company"),
				fieldname: "company",
				fieldtype: "Link",
				options: "Company",
				reqd: 1,
			},
			{
				label: __("Payment Account"),
				fieldname: "payment_account",
				fieldtype: "Link",
				options: "Account",
				reqd: 1,
				description: __("Bank or cash account used to collect payments."),
			},
			{
				label: __("Currency"),
				fieldname: "currency",
				fieldtype: "Link",
				options: "Currency",
			},
			{
				label: __("Set as Default"),
				fieldname: "is_default",
				fieldtype: "Check",
			},
		],
		body: formWrapper,
		frm: frm,
	});
	fieldGroup.make();

	// --- Footer ---
	overlay.bottomSection.innerHTML = "";
	const footer = document.createElement("div");
	footer.classList.add("gws-overlay-footer");
	footer.innerHTML = `
		<button class="btn btn-primary btn-sm" data-action="save">${__("Save")}</button>
		<button class="btn btn-default btn-sm" data-action="cancel">${__("Cancel")}</button>`;
	overlay.bottomSection.appendChild(footer);

	footer.querySelector("[data-action='save']").addEventListener("click", () => {
		const values = fieldGroup.get_values();
		if (!values) return;

		frappe.call({
			method: "payments.utils.gateway_setup.create_gateway_account",
			args: {
				payment_gateway: data.payment_gateway,
				company: values.company,
				payment_account: values.payment_account,
				currency: values.currency || null,
				is_default: values.is_default ? 1 : 0,
			},
			callback(r) {
				if (r.exc) return;
				frappe.show_alert({ message: __("Payment account added"), indicator: "green" });
				overlay.close();
				payments.render_gateway_setup_section(frm);
			},
		});
	});

	footer.querySelector("[data-action='cancel']").addEventListener("click", () => overlay.close());

	overlay.open();
};

// ── Configure Mode of Payment overlay ───────────────────────────────────────

payments._open_configure_mop_overlay = function (frm, data) {
	if (!payments._mop_overlay) {
		const parent = $(frm.page.wrapper).find(".page-overlay");
		payments._mop_overlay = new frappe.views.dodock_drawer({
			title: __("Modes of Payment"),
			parent: parent,
			animation_type: "slide",
			backdrop: true,
			open_width: "420px",
		});
	}

	const overlay = payments._mop_overlay;

	overlay.mainSection.innerHTML = "";
	const body = document.createElement("div");
	body.classList.add("gws-overlay-body");
	overlay.mainSection.appendChild(body);

	const gwName = data.payment_gateway || frm.doc.name;
	const step3 = __("Set {0} = {1}.", [
		"<strong>" + __("Payment Gateway") + "</strong>",
		"<em>" + gwName + "</em>",
	]);

	body.innerHTML = `
		<p class="text-muted mb-3" style="font-size:12px">
			${__("Each company that uses this gateway needs a Mode of Payment with the gateway linked and a payment account configured.")}
		</p>
		<ol class="pl-3 text-muted" style="font-size:12px;line-height:1.7">
			<li>${__("Open or create a {0}.", ["<strong>" + __("Mode of Payment") + "</strong>"])}</li>
			<li>${__("In the {0} table, add a row for your company.", ["<em>" + __("Accounts") + "</em>"])}</li>
			<li>${step3}</li>
			<li>${__("Set the {0}, and optionally fee/tax accounts.", ["<strong>" + __("Default Account") + "</strong>"])}</li>
			<li>${__("Save the Mode of Payment.")}</li>
		</ol>
		<div class="mt-4 d-flex gap-2 flex-wrap">
			<a href="/app/mode-of-payment" target="_blank" class="btn btn-sm btn-default">
				${frappe.utils.icon("list", "xs")} ${__("Browse Modes of Payment")}
			</a>
			<a href="/app/mode-of-payment/new-mode-of-payment-1" target="_blank" class="btn btn-sm btn-primary">
				${frappe.utils.icon("add", "xs")} ${__("Create New")}
			</a>
		</div>`;

	overlay.bottomSection.innerHTML = "";
	const footer = document.createElement("div");
	footer.classList.add("gws-overlay-footer");
	footer.innerHTML = `<button class="btn btn-default btn-sm" data-action="close">${__("Close")}</button>`;
	footer.querySelector("[data-action='close']").addEventListener("click", () => {
		overlay.close();
		payments.render_gateway_setup_section(frm);
	});
	overlay.bottomSection.appendChild(footer);

	overlay.open();
};
